import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { SectionProps } from './types';

export const FrameworksSection: React.FC<Pick<SectionProps, 'assumptions' | 'formatCurrency'>> = ({
  assumptions,
  formatCurrency
}) => {
  return (
    <ReportSection id="frameworks" title="Thinking Frameworks" subtitle="How to decide when you can't predict">
      <p className="text-gray-700 mb-6">
        Since we can't reliably predict the future, how should you think about this decision? Here are
        some frameworks that might help.
      </p>

      <ReportSubsection title="The Regret Minimization Framework">
        <p className="text-gray-700 mb-4">
          Jeff Bezos famously used this to decide to start Amazon: "When I'm 80, which choice will I
          regret more?"
        </p>

        <p className="text-gray-700 mb-4">
          Applied here: Imagine it's 2035. Newport Beach prices have doubled. Would you regret selling?
          Now imagine prices are flat and stocks are up 150%. Would you regret keeping?
        </p>

        <p className="text-gray-700 mb-4">
          For many people, asymmetric regret makes the choice clearer. Missing out on Newport Beach gains
          might hurt more than missing out on stock gains (or vice versa), even if the expected values are similar.
        </p>
      </ReportSubsection>

      <ReportSubsection title="The Optionality Lens">
        <p className="text-gray-700 mb-4">
          Optionality means keeping your future choices open. The two options have different optionality
          profiles:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
          <li><strong>Keeping:</strong> You can always sell later. You can do a 1031 exchange into different
          property. You can move into it. You can give it to family.</li>
          <li><strong>Selling:</strong> The money is fully liquid. You can invest in anything. You can
          spend it. But you can't easily get back into Newport Beach real estate at today's prices.</li>
        </ul>

        <p className="text-gray-700">
          Which options matter more to you? If you might want to return to Newport Beach someday, keeping
          the property preserves that option. If you value financial flexibility above all, selling provides it.
        </p>
      </ReportSubsection>

      <ReportSubsection title="Personal Risk Capacity">
        <p className="text-gray-700 mb-4">
          Risk capacity isn't just about psychology—it's about your actual financial situation:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
          <li><strong>How much of your net worth is this property?</strong> If it's 80%, that's a lot of
          concentration. If it's 20%, you already have diversification.</li>
          <li><strong>Do you have other real estate?</strong> If you own your home, you already have real
          estate exposure.</li>
          <li><strong>What's your income stability?</strong> Landlording is easier to stomach with a stable
          high income to cover any cash flow gaps.</li>
          <li><strong>What's your time horizon?</strong> If you might need the money in 5 years, stocks
          are risky. If your horizon is 20+ years, both options have time to recover from bad years.</li>
        </ul>
      </ReportSubsection>

      <ReportSubsection title="The Sleep Test">
        <p className="text-gray-700 mb-4">
          Sometimes the best framework is the simplest: which option lets you sleep better at night?
        </p>

        <p className="text-gray-700 mb-4">
          If the thought of being a landlord stresses you out—dealing with tenants, worrying about
          maintenance, checking rental listings—sell. The money you lose in potentially-lower returns
          is worth the peace of mind.
        </p>

        <p className="text-gray-700">
          If watching {formatCurrency(assumptions.propertyValue)} in stocks drop 30% would cause you anxiety attacks—even knowing it'll probably
          recover—keep the property. Real estate feels more stable (even if it's not always) because you
          don't see daily price quotes.
        </p>
      </ReportSubsection>
    </ReportSection>
  );
};
